"use client";

import { LogOut } from "lucide-react";
import { useRouter } from "next/navigation";
import { signOut } from "next-auth/react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { cn } from "@/lib/utils";

type LogoutButtonProps = {
  children?: React.ReactNode;
  className?: string;
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link";
  size?: "default" | "sm" | "lg" | "icon";
  showIcon?: boolean;
  redirectTo?: string;
  confirmLogout?: boolean;
  onLogoutStart?: () => void;
  onLogoutComplete?: () => void;
  onLogoutError?: (error: unknown) => void;
};

/**
 * Reusable logout button component with customizable appearance and behavior
 */
export function LogoutButton({
  children,
  className,
  variant = "ghost",
  size = "default",
  showIcon = true,
  redirectTo = "/",
  confirmLogout = false,
  onLogoutStart,
  onLogoutComplete,
  onLogoutError,
}: LogoutButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const _router = useRouter();

  const handleLogout = async () => {
    if (confirmLogout) {
      const confirmed = window.confirm("Are you sure you want to sign out?");
      if (!confirmed) return;
    }

    setIsLoading(true);
    onLogoutStart?.();

    try {
      await signOut({
        callbackUrl: redirectTo,
        redirect: true,
      });

      toast.success("Signed out successfully");
      onLogoutComplete?.();
    } catch (error) {
      toast.error("Failed to sign out");
      onLogoutError?.(error);
      setIsLoading(false);
    }
  };

  return (
    <Button
      className={cn(className)}
      disabled={isLoading}
      onClick={handleLogout}
      size={size}
      variant={variant}
    >
      {isLoading ? (
        <>
          <LoadingSpinner className={showIcon ? "mr-2" : ""} size="sm" />
          {children || "Signing Out..."}
        </>
      ) : (
        <>
          {showIcon && <LogOut className="mr-2 h-4 w-4" />}
          {children || "Sign Out"}
        </>
      )}
    </Button>
  );
}

/**
 * Logout button specifically for dropdown menus
 */
export function LogoutMenuItem({
  className,
  onLogoutStart,
  onLogoutComplete,
  onLogoutError,
}: {
  className?: string;
  onLogoutStart?: () => void;
  onLogoutComplete?: () => void;
  onLogoutError?: (error: unknown) => void;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const handleLogout = async () => {
    setIsLoading(true);
    onLogoutStart?.();

    try {
      await signOut({
        callbackUrl: "/",
        redirect: true,
      });

      toast.success("Signed out successfully");
      onLogoutComplete?.();
    } catch (error) {
      toast.error("Failed to sign out");
      onLogoutError?.(error);
      setIsLoading(false);
    }
  };

  return (
    <button
      className={cn(
        "flex w-full items-center px-2 py-1.5 text-sm transition-colors hover:bg-accent focus:bg-accent focus:outline-none disabled:pointer-events-none disabled:opacity-50",
        className
      )}
      disabled={isLoading}
      onClick={handleLogout}
      type="button"
    >
      {isLoading ? (
        <>
          <LoadingSpinner className="mr-2" size="sm" />
          Signing Out...
        </>
      ) : (
        <>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </>
      )}
    </button>
  );
}
